<template>
  <div class="order-history-screen">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-message">
      {{ t('labels.Loading') }}...
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="loadOrderHistory" class="retry-button">
        {{ t('buttons.Retry') }}
      </button>
    </div>
    
    <!-- Orders List -->
    <div v-else-if="orders.length > 0" class="orders-container">
      <div class="orders-header">
        <div class="header-content">
          <h3>{{ t('accountInfo.orderHistory.ordersFound', { count: orders.length }) }}</h3>
          <div class="pagination-info">
            {{ t('accountInfo.orderHistory.showingResults', { 
              start: (currentPage - 1) * pageSize + 1, 
              end: Math.min(currentPage * pageSize, orders.length), 
              total: orders.length 
            }) }}
          </div>
        </div>
        <div class="page-size-selector">
          <label for="pageSize">{{ t('accountInfo.orderHistory.itemsPerPage') }}:</label>
          <select id="pageSize" v-model="pageSize" @change="currentPage = 1">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>
      
      <div class="orders-list">
        <div 
          v-for="order in paginatedOrders" 
          :key="order.aoeOrderId"
          class="order-item"
          :class="{ 'expanded': selectedOrder?.aoeOrderId === order.aoeOrderId }"
          @click="toggleOrderDetails(order)"
        >
          <div class="order-header">
            <div class="order-info">
              <div class="order-id">
                <strong>{{ t('accountInfo.orderHistory.orderNumber') }}:</strong> 
                #{{ order.aoeOrderId }}
              </div>
              <div class="order-date">
                <strong>{{ t('accountInfo.orderHistory.orderDate') }}:</strong> 
                {{ formatDate(order.orderTime) }}
              </div>
            </div>
            <div class="order-header-actions">
              <div class="order-total">
                {{ formatCurrency(order.total, order.currencyId) }}
              </div>
              <button
                v-if="order.aoeOrderIdToken"
                @click.stop="openOrderReceipt(order.aoeOrderIdToken)"
                class="view-receipt-button"
              >
                {{ t('accountInfo.orderHistory.viewReceipt') }}
              </button>
            </div>
          </div>
          
          <div class="order-details">
            <div class="order-type">
              <strong>{{ t('accountInfo.orderHistory.orderType') }}:</strong> 
              {{ order.orderTypeText }}
            </div>
            
            <div v-if="order.trackingNumber" class="tracking-info">
              <strong>{{ t('accountInfo.orderHistory.tracking') }}:</strong>
              <a 
                v-if="order.trackingNumberUrl" 
                :href="order.trackingNumberUrl" 
                target="_blank" 
                class="tracking-link"
              >
                {{ order.trackingNumber }}
              </a>
              <span v-else>{{ order.trackingNumber }}</span>
            </div>
            
            <div v-if="order.commissionableValue > 0" class="commission-info">
              <strong>{{ t('accountInfo.orderHistory.commissionValue') }}:</strong> 
              {{ formatCurrency(order.commissionableValue, order.currencyId) }}
            </div>
          </div>

          <!-- Expanded Order Details -->
          <div v-if="selectedOrder?.aoeOrderId === order.aoeOrderId" class="order-expanded-details">
            <!-- Loading State -->
            <div v-if="loadingDetails.has(order.aoeOrderIdToken || '')" class="loading-details">
              <div class="loading-spinner"></div>
              <p>{{ t('labels.Loading') }}...</p>
            </div>
            
            <div v-else class="expanded-sections">
              <!-- Customer Information -->
              <div v-if="order.customer" class="detail-section">
                <h3>{{ t('accountInfo.orderHistory.customerInformation') }}</h3>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">{{ t('accountInfo.orderHistory.customerName') }}:</span>
                    <span class="detail-value">{{ order.customer.firstName }} {{ order.customer.lastName }}</span>
                  </div>
                  <div v-if="order.customer.email" class="detail-item">
                    <span class="detail-label">{{ t('accountInfo.orderHistory.customerEmail') }}:</span>
                    <span class="detail-value">{{ order.customer.email }}</span>
                  </div>
                </div>
              </div>

              <!-- Product Details (from API call) -->
              <div v-if="selectedOrderDetails?.item.skus && selectedOrderDetails.item.skus.length > 0" class="detail-section">
                <h3>{{ t('accountInfo.orderHistory.productDetails') }}</h3>
                <div class="products-table">
                  <div class="products-header">
                    <div class="product-col-image">{{ t('accountInfo.orderHistory.productImage') }}</div>
                    <div class="product-col-name">{{ t('accountInfo.orderHistory.productName') }}</div>
                    <div class="product-col-qty">{{ t('accountInfo.orderHistory.quantity') }}</div>
                    <div class="product-col-price">{{ t('accountInfo.orderHistory.price') }}</div>
                    <div class="product-col-total">{{ t('accountInfo.orderHistory.lineTotal') }}</div>
                  </div>
                  <div 
                    v-for="sku in selectedOrderDetails.item.skus" 
                    :key="sku.lineNumber"
                    class="product-row"
                  >
                    <div class="product-col-image">
                      <img
                        :src="getProductImageUrl(sku.productCode)"
                        class="product-image"
                        :alt="sku.productName"
                        loading="lazy"
                        @error="handleImageError"
                      />
                    </div>
                    <div class="product-col-name">
                      <div class="product-name">{{ sku.productName }}</div>
                      <div class="sku-code">{{ sku.skuCode }}</div>
                    </div>
                    <div class="product-col-qty">{{ sku.quantity }}</div>
                    <div class="product-col-price">{{ formatCurrency(sku.price, order.currencyId) }}</div>
                    <div class="product-col-total">{{ formatCurrency(sku.price * sku.quantity, order.currencyId) }}</div>
                  </div>
                </div>
              </div>

              <!-- Shipping & Billing Information Section -->
              <div v-if="selectedOrderDetails?.item.shippingAddress" class="detail-section">
                <h3>{{ t('accountInfo.orderHistory.shippingInformation') }}</h3>
                <div class="address-grid">
                  <div class="address-column">
                    <div class="detail-item">
                      <span class="detail-label">{{ t('accountInfo.orderHistory.shippingTo') }}:</span>
                      <div class="detail-value shipping-address-block">
                        <div class="address-name">{{ selectedOrderDetails.item.shippingAddress.firstName }} {{ selectedOrderDetails.item.shippingAddress.lastName }}</div>
                        <div class="address-line">
                          {{ selectedOrderDetails.item.shippingAddress.addressLine1 }}
                          <span v-if="selectedOrderDetails.item.shippingAddress.houseNumber"> {{ selectedOrderDetails.item.shippingAddress.houseNumber }}</span>
                        </div>
                        <div v-if="selectedOrderDetails.item.shippingAddress.addressLine2" class="address-line">{{ selectedOrderDetails.item.shippingAddress.addressLine2 }}</div>
                        <div class="address-city">{{ selectedOrderDetails.item.shippingAddress.city }}, {{ selectedOrderDetails.item.shippingAddress.postalCode }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="address-column">
                    <div class="detail-item">
                      <span class="detail-label">{{ t('accountInfo.orderHistory.billingAddress') }}:</span>
                      <div class="detail-value shipping-address-block">
                        <div class="address-name">{{ getBillingAddress().firstName }} {{ getBillingAddress().lastName }}</div>
                        <div class="address-line">
                          {{ getBillingAddress().addressLine1 }}
                          <span v-if="getBillingAddress().houseNumber"> {{ getBillingAddress().houseNumber }}</span>
                        </div>
                        <div v-if="getBillingAddress().addressLine2" class="address-line">{{ getBillingAddress().addressLine2 }}</div>
                        <div class="address-city">{{ getBillingAddress().city }}, {{ getBillingAddress().postalCode }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Shipping Method - moved below addresses -->
                <div v-if="selectedOrderDetails.item.shippingMethodName" class="detail-item shipping-method-below">
                  <span class="detail-label">{{ t('accountInfo.orderHistory.shippingMethod') }}:</span>
                  <span class="detail-value">{{ selectedOrderDetails.item.shippingMethodName }}</span>
                </div>
              </div>

              <!-- Payment Details Section -->
              <div v-if="selectedOrderDetails" class="detail-section">
                <h3>{{ t('accountInfo.orderHistory.paymentDetails') }}</h3>
                <!-- Payment Method Information -->
                <div v-if="selectedOrderDetails.item.payments && selectedOrderDetails.item.payments.length > 0" class="payment-methods">
                  <h4>{{ t('accountInfo.orderHistory.paymentMethod') }}</h4>
                  <div 
                    v-for="payment in selectedOrderDetails.item.payments" 
                    :key="payment.paymentId"
                    class="payment-method-card"
                  >
                    <div class="payment-info">
                      <div class="payment-card-info">
                        <span class="card-type">{{ payment.attributes.cardType }}</span>
                        <span class="card-number">**** **** **** {{ payment.attributes.lastFour }}</span>
                        <span class="card-expiry">{{ payment.attributes.expirationMonth }}/{{ payment.attributes.expirationYear }}</span>
                      </div>
                      <div v-if="payment.attributes.cardholderName" class="cardholder-name">
                        {{ payment.attributes.cardholderName }}
                      </div>
                      <div class="payment-amount">
                        {{ formatCurrency(payment.charge, order.currencyId) }}
                      </div>
                    </div>
                    <div class="payment-status">
                      <span v-if="payment.isBooked" class="status-success">
                        {{ t('accountInfo.orderHistory.paymentProcessed') }}
                      </span>
                      <span v-else-if="payment.isAuthorized" class="status-pending">
                        {{ t('accountInfo.orderHistory.paymentAuthorized') }}
                      </span>
                      <span v-if="payment.isRefunded" class="status-refunded">
                        {{ t('accountInfo.orderHistory.paymentRefunded') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Order Total Breakdown -->
              <div v-if="selectedOrderDetails" class="detail-section">
                <h3>{{ t('accountInfo.orderHistory.orderSummary') }}</h3>
                <div class="order-breakdown">
                  <div class="breakdown-item">
                    <span class="breakdown-label">{{ t('accountInfo.orderHistory.subtotal') }}:</span>
                    <span class="breakdown-value">{{ formatCurrency((selectedOrderDetails.item.subtotal || calculateSubtotal()) + getTotalDiscountAmount(), order.currencyId) }}</span>
                  </div>
                  <div class="breakdown-item discount">
                    <span class="breakdown-label">{{ t('accountInfo.orderHistory.discount') }}:</span>
                    <span class="breakdown-value">-{{ formatCurrency(getTotalDiscountAmount(), order.currencyId) }}</span>
                  </div>
                  <div class="breakdown-item">
                    <span class="breakdown-label">{{ t('accountInfo.orderHistory.shippingAndHandling') }}:</span>
                    <span class="breakdown-value">{{ formatCurrency(selectedOrderDetails.item.shippingCost || 0, order.currencyId) }}</span>
                  </div>
                  <div class="breakdown-separator"></div>
                  <div class="breakdown-item total">
                    <span class="breakdown-label">{{ t('accountInfo.orderHistory.inTotal') }}:</span>
                    <span class="breakdown-value">{{ formatCurrency(selectedOrderDetails.item.total, order.currencyId) }}</span>
                  </div>
                </div>
              </div>

              <!-- Tracking Information -->
              <div v-if="order.trackingNumber" class="detail-section">
                <h3>{{ t('accountInfo.orderHistory.shippingInformation') }}</h3>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">{{ t('accountInfo.orderHistory.tracking') }}:</span>
                    <span class="detail-value">
                      <a 
                        v-if="order.trackingNumberUrl" 
                        :href="order.trackingNumberUrl" 
                        target="_blank" 
                        class="tracking-link"
                      >
                        {{ order.trackingNumber }}
                      </a>
                      <span v-else>{{ order.trackingNumber }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Pagination Controls -->
      <div v-if="totalPages > 1" class="pagination-container">
        <div class="pagination-controls">
          <button 
            @click="prevPage" 
            :disabled="currentPage === 1"
            class="pagination-btn"
          >
            {{ t('buttons.Previous') }}
          </button>
          
          <div class="page-numbers">
            <button
              v-for="page in getVisiblePages()"
              :key="page"
              @click="goToPage(page)"
              :class="['page-number', { 'active': page === currentPage }]"
            >
              {{ page }}
            </button>
          </div>
          
          <button 
            @click="nextPage" 
            :disabled="currentPage === totalPages"
            class="pagination-btn"
          >
            {{ t('buttons.Next') }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- No Orders State -->
    <div v-else class="no-orders">
      <div class="no-orders-content">
        <h3>{{ t('accountInfo.orderHistory.noOrders') }}</h3>
        <p>{{ t('accountInfo.orderHistory.noOrdersDescription') }}</p>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useOrderHistory } from '../../composables/useOrderHistory'
import type { IOrderListItem } from '../../types/OrderTypes'
import { GetFullProductImageDictionary } from '../../services/SkuService'
import { GetOrderReceiptUrl } from '../../stores/SettingsStore'

const { t } = useI18n()

const { 
  orders, 
  isLoading, 
  error, 
  loadOrderHistory, 
  formatCurrency, 
  formatDate,
  orderDetails,
  loadingDetails,
  loadOrderDetails
} = useOrderHistory()

// Pagination state
const currentPage = ref<number>(1)
const pageSize = ref<number>(10)

// Order detail expanded state
const selectedOrder = ref<IOrderListItem | null>(null)

// Function to open order receipt in new tab
const openOrderReceipt = async (orderToken: string) => {
  const receiptBaseUrl = await GetOrderReceiptUrl()
  if (!receiptBaseUrl) {
    console.warn('orderReceiptBaseUrl not provided')
    return
  }
  const receiptUrl = `${receiptBaseUrl}?id=${orderToken}`
  window.open(receiptUrl, '_blank')
}

// Product image URLs for SKUs
const productImageUrls = ref<Record<string, string>>({})

// Computed properties for pagination
const totalPages = computed(() => Math.ceil(orders.value.length / pageSize.value))

const paginatedOrders = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  const end = start + pageSize.value
  return orders.value.slice(start, end)
})

// Get detailed order information for the selected order
const selectedOrderDetails = computed(() => {
  if (!selectedOrder.value?.aoeOrderIdToken) return null
  return orderDetails.value.get(selectedOrder.value.aoeOrderIdToken) || null
})

// Pagination methods
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const getVisiblePages = () => {
  const pages = []
  const maxVisiblePages = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxVisiblePages / 2))
  const endPage = Math.min(totalPages.value, startPage + maxVisiblePages - 1)
  
  // Adjust startPage if we're near the end
  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1)
  }
  
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }
  
  return pages
}

// Helper function to get product image URL
function getProductImageUrl(productCode: string): string {
  return productImageUrls.value[productCode] || '/images/placeholder-product.png'
}

// Handle image loading errors
function handleImageError(event: Event) {
  const img = event.target as HTMLImageElement
  if (img && img.src !== '/images/placeholder-product.png') {
    img.src = '/images/placeholder-product.png'
  }
}

// Calculate subtotal from SKU prices
function calculateSubtotal(): number {
  if (!selectedOrderDetails.value?.item.skus) return 0
  return selectedOrderDetails.value.item.skus.reduce((sum, sku) => sum + (sku.price * sku.quantity), 0)
}

// Get total discount amount from all SKUs
function getTotalDiscountAmount(): number {
  if (!selectedOrderDetails.value?.item.skus) return 0
  return selectedOrderDetails.value.item.skus.reduce((sum, sku) => sum + (sku.totalDiscountAmount || 0), 0)
}

// Get billing address (use billing address if available, otherwise use shipping address)
function getBillingAddress() {
  if (!selectedOrderDetails.value?.item) return { firstName: '', lastName: '', addressLine1: '', addressLine2: '', houseNumber: '', city: '', postalCode: '' }
  return selectedOrderDetails.value.item.billingAddress || selectedOrderDetails.value.item.shippingAddress || { firstName: '', lastName: '', addressLine1: '', addressLine2: '', houseNumber: '', city: '', postalCode: '' }
}

// Load product images for order SKUs
const loadProductImages = async (skus: Array<{ productCode: string }>) => {
  const productCodes = skus.map(sku => sku.productCode).filter(Boolean)
  if (productCodes.length > 0) {
    const imageUrls = await GetFullProductImageDictionary(productCodes)
    productImageUrls.value = { ...productImageUrls.value, ...imageUrls }
  }
}

// Order detail toggle methods
const toggleOrderDetails = async (order: IOrderListItem) => {
  if (selectedOrder.value?.aoeOrderId === order.aoeOrderId) {
    // If clicking the same order, collapse it
    selectedOrder.value = null
  } else {
    // If clicking a different order, expand it
    selectedOrder.value = order
    
    // Load detailed order information if we have the token
    if (order.aoeOrderIdToken) {
      const orderDetail = await loadOrderDetails(order.aoeOrderIdToken)
      
      // Load product images for the SKUs
      if (orderDetail?.item.skus) {
        await loadProductImages(orderDetail.item.skus)
      }
    }
  }
}

onMounted(async () => {
  await loadOrderHistory()
})
</script>

<style lang="scss" scoped>
.order-history-screen {
  width: 100%;
  padding: $card-padding;
}

.loading-message {
  @include typography-body;
  text-align: center;
  padding: $spacing-10;
  color: #666;
}

.error-message {
  text-align: center;
  padding: $spacing-10;
  color: #dc3545;
}

.retry-button {
  @include typography-small;
  margin-top: $spacing-4;
  padding: $spacing-2 $spacing-4;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  border-radius: $button-radius-lg;
  cursor: pointer;
}

.retry-button:hover {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.orders-container {
  width: 100%;
}

.orders-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $spacing-5;
}

.header-content h3 {
  @include typography-h4;
  margin: 0 0 8px 0;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.pagination-info {
  @include typography-small;
  color: #666;
  margin: 0;
}

.page-size-selector {
  @include typography-small;
  display: flex;
  align-items: center;
  gap: $gap-sm;
  min-width: 180px;
  flex-shrink: 0;
}

.page-size-selector label {
  @include font-weight-medium;
  color: #666;
  white-space: nowrap;
}

.page-size-selector select {
  @include typography-small;
  padding: $spacing-2 $spacing-2;
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  background: white;
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: $gap-md;
}

.order-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $card-padding;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
}

.order-item:hover {
  box-shadow: 0 4px 8px rgba(black, 0.2);
  transform: translateY(-1px);
}

.order-item.expanded {
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  box-shadow: 0 4px 12px rgba(var(--vt-c-teal-dark), 0.15);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--vt-c-white-soft);
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.order-id, .order-date {
  @include typography-small;
  color: #333;
}

.order-header-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: $spacing-2;
}

.order-total {
  @include typography-body-large;
  @include font-weight-semibold;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.view-receipt-button {
  @include typography-small;
  padding: $spacing-2 $spacing-3;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.view-receipt-button:hover {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.order-details {
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.order-type, .customer-info, .tracking-info, .commission-info {
  @include typography-small;
  color: #666;
}

.customer-email {
  @include typography-small;
  color: #888;
}

.tracking-link {
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  text-decoration: none;
}

.tracking-link:hover {
  text-decoration: underline;
}

.pagination-container {
  margin-top: 32px;
  display: flex;
  justify-content: center;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: $gap-sm;
}

.pagination-btn {
  padding: $spacing-2 $spacing-4;
  border: 1px solid #e0e0e0;
  background: white;
  @include typography-small;
  color: #333;
  border-radius: $radius-sm;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: $spacing-1;
  margin: 0 $spacing-2;
}

.page-number {
  padding: $spacing-2 $spacing-3;
  border: 1px solid #e0e0e0;
  background: white;
  @include typography-small;
  color: #333;
  border-radius: $radius-sm;
  cursor: pointer;
  min-width: 40px;
  text-align: center;
  transition: all 0.2s ease;
}

.page-number:hover {
  background-color: var(--vt-c-white-soft);
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.page-number.active {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.no-orders {
  text-align: center;
  padding: $spacing-16 $spacing-5;
}

.no-orders-content h3 {
  color: #666;
  margin-bottom: $spacing-3;
}

.no-orders-content p {
  @include typography-small;
  color: #888;
}

@media (max-width: 768px) {
  .order-history-screen {
    padding: $spacing-4;
  }
  
  .orders-header {
    flex-direction: column;
    gap: $gap-md;
    align-items: stretch;
  }
  
  .page-size-selector {
    justify-content: space-between;
  }
  
  .order-header {
    flex-direction: column;
    gap: $spacing-3;
    align-items: flex-start;
  }
  
  .order-total {
    @include typography-body;
  }
  
  .order-info {
    width: 100%;
  }
  
  .pagination-controls {
    flex-wrap: wrap;
    gap: $spacing-1;
  }
  
  .pagination-btn {
    @include typography-small;
    padding: $spacing-2 $spacing-3;
  }
  
  .page-number {
    @include typography-small;
    padding: $spacing-2 $spacing-2-5;
    min-width: 36px;
  }
  
  .page-numbers {
    margin: 0 $spacing-1;
    gap: $spacing-1;
  }
}

/* Expanded Order Details Styles */
.order-expanded-details {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  animation: expandIn 0.3s ease-out;
}

@keyframes expandIn {
  from {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    margin-top: 0;
  }
  to {
    opacity: 1;
    max-height: 1000px;
    padding-top: 20px;
    margin-top: 20px;
  }
}

.expanded-sections {
  display: flex;
  flex-direction: column;
  gap: $spacing-5;
}

.detail-section {
  background: var(--vt-c-white-soft);
  border-radius: $radius-md;
  padding: $spacing-4;
  border: 1px solid #e9ecef;
}

.detail-section h3 {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 12px 0;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 8px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: $spacing-3;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.detail-label {
  @include typography-small;
  @include font-weight-semibold;
  color: #666;
}

.detail-value {
  @include typography-small;
  color: #333;
}

/* Mobile Styles for Expanded Details */
@media (max-width: 768px) {
  .order-expanded-details {
    margin-top: $spacing-4;
    padding-top: 16px;
  }
  
  .expanded-sections {
    gap: $gap-md;
  }
  
  .detail-section {
    padding: $spacing-3;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
    gap: $gap-sm;
  }
  
  .detail-section h3 {
    @include typography-small;
    margin-bottom: 10px;
  }
}

/* Loading Details Styles */
.loading-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: $spacing-10 $spacing-5;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-details-message {
  text-align: center;
  padding: $card-padding;
  color: #666;
  font-style: italic;
}

.error-message {
  text-align: center;
  padding: $card-padding;
  color: #dc3545;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: $radius-md;
  margin: $spacing-4 0;
}

.error-message p {
  @include font-weight-medium;
  margin: 0;
}

.status-badge {
  @include typography-small;
  @include font-weight-semibold;
  display: inline-block;
  padding: $spacing-1 $spacing-3;
  border-radius: $radius-xl;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
}

.total-row {
  @include font-weight-semibold;
  border-top: 2px solid #e9ecef;
  padding-top: 12px;
  margin-top: 8px;
}

.total-amount {
  @include typography-body-large;
  @include font-weight-bold;
  color: #000;
}

.payment-methods {
  margin-top: 24px;
}

.payment-methods h4 {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0 0 16px 0;
  color: #333;
}

.payment-method-card {
  background: var(--vt-c-white-soft);
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $spacing-4;
  margin-bottom: $spacing-3;
}

.payment-info {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: $spacing-3;
}

.payment-card-info {
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.card-type {
  @include typography-small;
  @include font-weight-semibold;
  color: #000;
}

.card-number {
  @include typography-small;
  font-family: monospace;
  color: #666;
}

.card-expiry {
  @include typography-caption;
  color: #666;
}

.cardholder-name {
  @include typography-small;
  color: #666;
  margin-top: 4px;
}

.payment-amount {
  @include typography-body;
  @include font-weight-semibold;
  color: #000;
}

.payment-status {
  display: flex;
  gap: $gap-sm;
  margin-bottom: $spacing-2;
}

.status-success {
  @include typography-caption;
  @include font-weight-medium;
  background-color: #d4edda;
  color: #155724;
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-sm;
}

.status-pending {
  @include typography-caption;
  @include font-weight-medium;
  background: #fff3cd;
  color: #856404;
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-sm;
  font-size: 12px;
  font-weight: 500;
}

.status-refunded {
  background: #f8d7da;
  color: #721c24;
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-sm;
  font-size: 12px;
  font-weight: 500;
}

.auth-details {
  color: #666;
  font-size: 12px;
}

.shipping-address-block {
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.address-name {
  font-weight: 600;
  color: #000;
}

.address-line,
.address-city {
  color: #666;
  font-size: 14px;
}

.address-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $spacing-8;
}

.address-column {
  display: flex;
  flex-direction: column;
  gap: $gap-md;
}

.shipping-method-below {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.shipping-method-below .detail-label {
  font-weight: 600;
  color: #333;
}

.shipping-method-below .detail-value {
  color: #666;
  font-weight: 500;
}

@media (max-width: 768px) {
  .address-grid {
    grid-template-columns: 1fr;
    gap: $spacing-6;
  }
}

.order-breakdown {
  background: var(--vt-c-white-soft);
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $card-padding;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-2 0;
  font-size: 14px;
}

.breakdown-label {
  color: #666;
  font-weight: 500;
}

.breakdown-value {
  color: #000;
  font-weight: 600;
}

.breakdown-item.discount .breakdown-value {
  color: #dc3545;
}

.breakdown-separator {
  border-top: 1px solid #dee2e6;
  margin: $spacing-3 0;
}

.breakdown-item.total {
  font-size: 16px;
  font-weight: 700;
  padding: $spacing-3 0 0 0;
}

.breakdown-item.total .breakdown-label {
  color: #000;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.breakdown-item.total .breakdown-value {
  font-size: 18px;
}

/* Products Table Styles */
.products-table {
  width: 100%;
  border-radius: $radius-md;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.products-header {
  display: grid;
  grid-template-columns: 80px 1fr 80px 100px 100px;
  gap: $gap-md;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  padding: $spacing-3 $spacing-4;
  font-weight: 600;
  font-size: 14px;
}

.products-header .product-col-image,
.products-header .product-col-name,
.products-header .product-col-qty,
.products-header .product-col-price,
.products-header .product-col-total {
  color: white !important;
}

.product-row {
  display: grid;
  grid-template-columns: 80px 1fr 80px 100px 100px;
  gap: $gap-md;
  padding: $spacing-3 $spacing-4;
  border-bottom: 1px solid var(--vt-c-white-soft);
  align-items: center;
}

.product-row:last-child {
  border-bottom: none;
}

.product-row:nth-child(even) {
  background-color: var(--vt-c-white-soft);
}

.product-col-image {
  display: flex;
  justify-content: center;
  align-items: center;
}

.product-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: $radius-sm;
  border: 1px solid #e9ecef;
}

.sku-code {
  font-family: monospace;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.product-col-name {
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.product-name {
  font-weight: 500;
  color: #000;
  font-size: 14px;
}

.product-code {
  font-size: 12px;
  color: #666;
  font-family: monospace;
}

.product-col-qty {
  text-align: center;
  font-weight: 500;
  color: #000;
}

.product-col-price,
.product-col-total {
  text-align: right;
  font-weight: 500;
  color: #000;
}

/* Mobile Responsive Products Table */
@media (max-width: 768px) {
  .products-header,
  .product-row {
    grid-template-columns: 1fr;
    gap: $gap-sm;
  }
  
  .products-header {
    display: none;
  }
  
  .product-row {
    display: block;
    padding: $spacing-4;
    border: 1px solid #e9ecef;
    border-radius: $radius-md;
    margin-bottom: $spacing-3;
    background: white;
  }
  
  .product-row:nth-child(even) {
    background: white;
  }
  
  .product-col-image {
    display: flex;
    justify-content: center;
    margin-bottom: $spacing-3;
  }
  
  .product-image {
    width: 80px;
    height: 80px;
  }
  
  .product-col-name,
  .product-col-qty,
  .product-col-price,
  .product-col-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $spacing-1 0;
    border-bottom: 1px solid var(--vt-c-white-soft);
  }
  
  .product-col-qty::before {
    content: 'Quantity: ';
    font-weight: 600;
    color: #666;
  }
  
  .product-col-price::before {
    content: 'Price: ';
    font-weight: 600;
    color: #666;
  }
  
  .product-col-total::before {
    content: 'Total: ';
    font-weight: 600;
    color: #666;
  }
  
  .product-col-name {
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-1;
    padding: $spacing-2 0;
  }
  
  .product-col-name::before {
    content: 'Product: ';
    font-weight: 600;
    color: #666;
    margin-bottom: 4px;
  }
}
</style>
